import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { useLanguage } from "@/contexts/LanguageContext";
import { useProjects } from "@/hooks/useProjects";
import debounce from "lodash/debounce";
import { formatNumber } from "@/lib/utils";
import projectPinImage from "@/icons/project-pin.png";
import getProjectTranslation from "@/utils/getProjectTranslation";

const MAPBOX_TOKEN =
  "pk.eyJ1IjoidHJlZS1seSIsImEiOiJja25rNG1heHgwNThjMnZwZTl2eThmeXUwIn0.SOrYUKfevZkbx8jZPxJesA";
const FALLBACK_BBOX =
  "-1.9950830850086163,44.4464186384987,21.995083085002875,54.12644342419196";

declare global {
  interface Window {
    projectMarkers?: mapboxgl.Marker[];
  }
}

const ProjectsOverviewClusteredMap: React.FC = () => {
  const { t, language } = useLanguage();
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const animationIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const { featureCollection, isLoading, handleBboxChange } = useProjects(500, {
    ignoreFilters: true,
  });

  const [userLocation, setUserLocation] = useState<{
    lat: number;
    lon: number;
  } | null>(null);

  const initialBboxRef = useRef<string | null>(null);
  const hasUpdatedInitialBbox = useRef(false);
  const isInitializing = useRef(true);

  const isBboxContained = useCallback(
    (innerBbox: string, outerBbox: string): boolean => {
      const [innerWest, innerSouth, innerEast, innerNorth] = innerBbox
        .split(",")
        .map(Number);
      const [outerWest, outerSouth, outerEast, outerNorth] = outerBbox
        .split(",")
        .map(Number);

      return (
        innerWest >= outerWest &&
        innerEast <= outerEast &&
        innerSouth >= outerSouth &&
        innerNorth <= outerNorth
      );
    },
    [],
  );

  // Debounced bbox update to prevent excessive updates
  const debouncedUpdateBbox = useMemo(() => {
    const updateBbox = () => {
      if (!map.current || !initialBboxRef.current) return;

      const bounds = map.current.getBounds();
      const newBbox = `${bounds.getWest()},${bounds.getSouth()},${bounds.getEast()},${bounds.getNorth()}`;

      if (!isBboxContained(newBbox, initialBboxRef.current)) {
        handleBboxChange(newBbox);
      }
    };

    return debounce(updateBbox, 500);
  }, [handleBboxChange, isBboxContained]);

  // Add or update projects layer
  const addProjectsLayer = useCallback(() => {
    if (!map.current || !featureCollection || !map.current.isStyleLoaded())
      return;

    const filteredFeatureCollection = {
      ...featureCollection,
      features: featureCollection.features.filter(
        (feature) => feature.properties?.isPublic !== false,
      ),
    };

    const source = map.current.getSource("projects") as mapboxgl.GeoJSONSource;

    if (!source) {
      // Add new source and layers
      map.current.addSource("projects", {
        type: "geojson",
        data: filteredFeatureCollection,
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 50,
      });

      // Add cluster circles
      map.current.addLayer({
        id: "clusters",
        type: "circle",
        source: "projects",
        filter: ["has", "point_count"],
        paint: {
          "circle-color": [
            "step",
            ["get", "point_count"],
            "#51bbd6",
            2,
            "#2A3FBA",
          ],
          "circle-radius": ["step", ["get", "point_count"], 20, 10, 30, 30, 40],
          "circle-radius-transition": { duration: 300 },
          "circle-stroke-width": 5,
          "circle-stroke-color": "#2A3FBA", // add 40% opacity
          "circle-stroke-opacity": 0.4,
        },
      });

      // Set up pulsing animation
      animationIntervalRef.current = setInterval(() => {
        if (!map.current) return;
        const now = Date.now() / 1000; // Current time in seconds
        const pulseFactor = 1 + 0.05 * Math.sin((now * 2 * Math.PI) / 2.8);
        const expression = [
          "step",
          ["get", "point_count"],
          20 * pulseFactor,
          10,
          30 * pulseFactor,
          30,
          40 * pulseFactor,
        ] as mapboxgl.ExpressionSpecification;
        map.current.setPaintProperty("clusters", "circle-radius", expression);
      }, 50); // Update every 50ms for smooth animation

      // Add cluster count text
      map.current.addLayer({
        id: "cluster-count",
        type: "symbol",
        source: "projects",
        filter: ["has", "point_count"],
        layout: {
          "text-field": "{point_count_abbreviated}",
          "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
          "text-size": 12,
        },
        paint: {
          "text-color": "#fff",
        },
      });

      map.current.loadImage(projectPinImage, (error, image) => {
        if (error) {
          console.error("Error loading project pin image:", error);
          return;
        }
        map.current.addImage("project-pin", image, { pixelRatio: 3 });
      });

      // Add individual project points
      map.current.addLayer({
        id: "unclustered-point",
        type: "symbol",
        source: "projects",
        filter: ["!", ["has", "point_count"]],
        layout: {
          "icon-image": "project-pin",
        },
      });

      // Add hover tooltip for unclustered points
      const popup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        className: "custom-popup",
      });

      // Add custom CSS for the popup
      const style = document.createElement("style");
      style.textContent = `
        .custom-popup .mapboxgl-popup-content {
          border-radius: 8px;
          padding: 12px;
        }
      `;
      document.head.appendChild(style);

      map.current.on("mouseenter", "unclustered-point", (e) => {
        // Change the cursor style when hovering
        map.current.getCanvas().style.cursor = "pointer";

        const coordinates = (e.features[0].geometry as any).coordinates.slice();
        const nameTranslations = e.features[0].properties.nameTranslations
          ? JSON.parse(e.features[0].properties.nameTranslations as string)
          : undefined;
        const title =
          getProjectTranslation(nameTranslations, language) ||
          e.features[0].properties.title;
        const projectDeveloper = JSON.parse(
          e.features[0].properties.projectDeveloper as string,
        );
        const developer = projectDeveloper.name;
        const area = formatNumber(
          e.features[0].properties.area / 10000,
          language,
        );

        const description = `
          <div style="padding: 2px;">
            <h3 style="margin: 0 0 5px 0; font-size: 14px; font-weight: bold;">${title}</h3>
            <p style="margin: 2px 0;"><strong>Area:</strong> ${area} ha</p>
            <p><strong>Project Developer:</strong> ${developer}</p>
          </div>
        `;

        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
          coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        popup.setLngLat(coordinates).setHTML(description).addTo(map.current);
      });

      map.current.on("mouseleave", "unclustered-point", () => {
        map.current.getCanvas().style.cursor = "";
        popup.remove();
      });

      // Add click event for clusters
      map.current.on("click", "clusters", (e) => {
        const features = map.current?.queryRenderedFeatures(e.point, {
          layers: ["clusters"],
        });

        if (!features || !features[0].properties) return;

        const clusterId = features[0].properties.cluster_id;
        const source = map.current?.getSource(
          "projects",
        ) as mapboxgl.GeoJSONSource;

        source.getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err || !map.current) return;

          const coordinates = (features[0].geometry as any).coordinates;

          map.current.easeTo({
            center: coordinates,
            zoom: zoom,
          });
        });
      });

      map.current.on("click", "unclustered-point", (e) => {
        if (!e.features || !e.features[0].properties?.id) return;
        window.location.href = `/projects/${e.features[0].properties.id}`;
      });

      // Change cursor on hover
      map.current.on("mouseenter", "clusters", () => {
        if (map.current) map.current.getCanvas().style.cursor = "pointer";
      });

      map.current.on("mouseleave", "clusters", () => {
        if (map.current) map.current.getCanvas().style.cursor = "";
      });

      map.current.on("mouseenter", "unclustered-point", () => {
        if (map.current) map.current.getCanvas().style.cursor = "pointer";
      });

      map.current.on("mouseleave", "unclustered-point", () => {
        if (map.current) map.current.getCanvas().style.cursor = "";
      });
    } else {
      // Update existing source data
      source.setData(filteredFeatureCollection);
    }
  }, [featureCollection, language]);

  // Initialize the map
  useEffect(() => {
    if (map.current || !mapContainer.current) return;

    mapboxgl.accessToken = MAPBOX_TOKEN;

    // Use fallback bbox if initialBboxRef is not set
    const bbox = initialBboxRef.current || FALLBACK_BBOX;
    const [west, south, east, north] = bbox.split(",").map(Number);
    const bounds = new mapboxgl.LngLatBounds([west, south], [east, north]);
    const center = bounds.getCenter();

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/streets-v12",
      center: [center.lng, center.lat],
      zoom: userLocation ? 10 : 6,
    });

    map.current.addControl(new mapboxgl.NavigationControl(), "top-right");

    map.current.on("load", () => {
      map.current?.fitBounds(bounds, { padding: 20 });

      map.current?.once("moveend", () => {
        if (!hasUpdatedInitialBbox.current) {
          const newBounds = map.current?.getBounds();
          if (newBounds) {
            const newBbox = `${newBounds.getWest()},${newBounds.getSouth()},${newBounds.getEast()},${newBounds.getNorth()}`;
            hasUpdatedInitialBbox.current = true;
            isInitializing.current = false;
            initialBboxRef.current = newBbox;
            handleBboxChange(newBbox);
          }
        }
      });
    });

    map.current.on("moveend", () => {
      if (!isInitializing.current && hasUpdatedInitialBbox.current) {
        debouncedUpdateBbox();
      }
    });

    return () => {
      debouncedUpdateBbox.cancel();
      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
      }
      map.current?.remove();
      map.current = null;
    };
  }, [debouncedUpdateBbox, handleBboxChange, userLocation]);

  // Request user location and set initial bbox
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLoc = {
            lat: position.coords.latitude,
            lon: position.coords.longitude,
          };
          setUserLocation(userLoc);
          const buffer = 1;
          const bbox = `${userLoc.lon - buffer},${userLoc.lat - buffer},${
            userLoc.lon + buffer
          },${userLoc.lat + buffer}`;
          initialBboxRef.current = bbox;
          handleBboxChange(bbox);
        },
        () => {
          initialBboxRef.current = FALLBACK_BBOX;
          handleBboxChange(FALLBACK_BBOX);
        },
      );
    } else {
      initialBboxRef.current = FALLBACK_BBOX;
      handleBboxChange(FALLBACK_BBOX);
    }
  }, [handleBboxChange]);

  useEffect(() => {
    if (map.current?.isStyleLoaded()) {
      addProjectsLayer();
    } else {
      map.current?.on("style.load", addProjectsLayer);
      return () => {
        map.current?.off("style.load", addProjectsLayer);
      };
    }
  }, [featureCollection, addProjectsLayer]);

  return (
    <>
      <div className="text-lg text-gray-700 mb-4">
        {t("map.exploreProjects")}
      </div>
      <div className="relative bg-white rounded-xl border border-gray-200 overflow-hidden h-[70vh]">
        <div ref={mapContainer} className="w-full h-full" />
      </div>
      {isLoading && <div>Loading projects...</div>}
    </>
  );
};

export default ProjectsOverviewClusteredMap;
